import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { isInIframe } from 'utils/env'
import { isPreviewMode, isUserLoggedIn } from 'utils/user'

export enum AnalyticsSurface {
  FULL_APP = 'full_app',
  GUEST = 'guest',
  EMBED = 'embed'
}

export const GUEST_ANALYTICS_SENTINEL_ID = 'guest_mode'
export const EMBED_ANALYTICS_SENTINEL_ID = 'embed_mode'
export const IN_APP_EMBED_PREVIEW_PARAM = 'inAppPreview'

export function isInAppEmbedPreview(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    new URLSearchParams(window.location.search).get(
      IN_APP_EMBED_PREVIEW_PARAM
    ) === '1'
  )
}

function isPublicCalendarView(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const { pathname, search } = window.location
  const hasPublicCalendarPath =
    pathname === '/calendar' || pathname.startsWith('/calendar/')
  const hasPublicGroupIds = Boolean(new URLSearchParams(search).get('groupIds'))

  return hasPublicCalendarPath || hasPublicGroupIds
}

function isAuthenticatedSession(): boolean {
  return isUserLoggedIn() || !!LocalStorage.get(LocalStorageKey.ACCESS_TOKEN)
}

export function getAnalyticsSurface(): AnalyticsSurface {
  if (typeof window === 'undefined') {
    return AnalyticsSurface.FULL_APP
  }

  // True embed widgets use preview=1. AuthProvider only shows GuestMode for
  // authenticated users in that case, so keep embed classification first.
  if (isPreviewMode()) {
    return AnalyticsSurface.EMBED
  }

  // Logged-in /calendars (and any other authenticated session) is the full app,
  // even if the URL has groupIds or the page happens to be in an iframe.
  if (isAuthenticatedSession()) {
    return AnalyticsSurface.FULL_APP
  }

  const isPublicView = isPublicCalendarView()

  if (isPublicView && isInIframe()) {
    return AnalyticsSurface.EMBED
  }

  if (isPublicView) {
    return AnalyticsSurface.GUEST
  }

  return AnalyticsSurface.FULL_APP
}

export function isPublicAnalyticsSurface(
  surface: AnalyticsSurface = getAnalyticsSurface()
): boolean {
  return (
    surface === AnalyticsSurface.GUEST || surface === AnalyticsSurface.EMBED
  )
}

export function getAnalyticsSentinelId(
  surface: AnalyticsSurface = getAnalyticsSurface()
): string | undefined {
  if (surface === AnalyticsSurface.GUEST) {
    return GUEST_ANALYTICS_SENTINEL_ID
  }

  if (surface === AnalyticsSurface.EMBED) {
    return EMBED_ANALYTICS_SENTINEL_ID
  }

  return undefined
}
